import { Availability, CartItem, Category, Item, ItemOffer, OptionGroup } from "@/types"
import { OrderResponse } from "@/types/external"
import { computeItemTotal } from "@/lib/pricing"
import { normalizePortionGroups } from "@/lib/option-group-portions"
import { findCategoryForOffer } from "@/lib/find-category-for-offer"
import { isOfferAvailableNow } from "@/lib/is-offer-available-now"
import { findMenuOfferForItem } from "@/lib/menu-offer"
import { isOptionAvailable } from "@/lib/is-option-available"
import {
  findInvalidOptionGroup,
  isOptionGroupAtCapacity,
  isOptionGroupBelowMinimum,
} from "@/lib/option-group-selection"
import { v4 as uuid } from "uuid"

type OrderItem = NonNullable<OrderResponse["items"]>[number]
type OrderItemOption = NonNullable<OrderItem["options"]>[number]

/**
 * Resolves a live catalog Item for an order line. Tries the most reliable
 * identifier first, then falls back through looser matches so historical
 * orders still reorder even when backend ids shift between deployments.
 */
function findCatalogItem(orderItem: OrderItem, items: Item[]): Item | null {
  const byItemId = orderItem.itemId && items.find((i) => i.id === orderItem.itemId)
  if (byItemId) return byItemId
  const byMerchantId =
    orderItem.merchantItemId && items.find((i) => i.id === orderItem.merchantItemId)
  if (byMerchantId) return byMerchantId
  const orderExternalCode = (orderItem as { externalCode?: string; ExternalCode?: string })
    .externalCode ?? (orderItem as { ExternalCode?: string }).ExternalCode
  if (orderExternalCode) {
    const byExternal = items.find((i) => i.externalCode === orderExternalCode)
    if (byExternal) return byExternal
  }
  if (orderItem.name) {
    const normalized = orderItem.name.trim().toLowerCase()
    const byName = items.find((i) => i.name?.trim().toLowerCase() === normalized)
    if (byName) return byName
  }
  return null
}

/**
 * Attempts to map a single option from a past order onto a live catalog
 * option group. Priority mirrors findCatalogItem: itemId, merchantItemId,
 * then the option's own catalog name. Only AVAILABLE options are
 * considered, unavailable ones return null so the caller can invalidate
 * the whole item offer. Availability reads BOTH the option and the item it
 * points at: the merchant's "Em falta" toggle lands on the ITEM for pizza
 * flavors.
 */
function findCatalogOption(
  group: OptionGroup,
  orderOption: OrderItemOption,
  items: Item[],
): { option: OptionGroup["options"][number]; item: Item } | null {
  const isAvailable = (opt: OptionGroup["options"][number]) =>
    isOptionAvailable(opt, items.find((i) => i.id === opt.itemId))

  for (const opt of group.options) {
    if (!isAvailable(opt)) continue
    if (orderOption.itemId && opt.itemId === orderOption.itemId) {
      const optItem = items.find((i) => i.id === opt.itemId)
      if (optItem) return { option: opt, item: optItem }
    }
  }
  for (const opt of group.options) {
    if (!isAvailable(opt)) continue
    if (orderOption.merchantItemId && opt.itemId === orderOption.merchantItemId) {
      const optItem = items.find((i) => i.id === opt.itemId)
      if (optItem) return { option: opt, item: optItem }
    }
  }
  const orderExternalCode = (orderOption as { externalCode?: string; ExternalCode?: string })
    .externalCode ?? (orderOption as { ExternalCode?: string }).ExternalCode
  if (orderExternalCode) {
    for (const opt of group.options) {
      if (!isAvailable(opt)) continue
      const optItem = items.find((i) => i.id === opt.itemId)
      if (optItem && optItem.externalCode === orderExternalCode) {
        return { option: opt, item: optItem }
      }
    }
  }
  if (orderOption.name) {
    const normalized = orderOption.name.trim().toLowerCase()
    for (const opt of group.options) {
      if (!isAvailable(opt)) continue
      const optItem = items.find((i) => i.id === opt.itemId)
      if (optItem && optItem.name?.trim().toLowerCase() === normalized) {
        return { option: opt, item: optItem }
      }
    }
  }
  return null
}

/**
 * Resolves the active, AVAILABLE catalog offer for a given item. Returns
 * null if the item is out of stock or no longer offered — we intentionally
 * do NOT fall back to unavailable offers for reorder, because we should
 * never add out-of-stock items to the cart.
 */
function findAvailableItemOffer(item: Item, itemOffers: ItemOffer[], categories: Category[]): ItemOffer | null {
  return findMenuOfferForItem(item.id, itemOffers, categories, (offer) => offer.status === "AVAILABLE")
}

export interface ReorderResult {
  cartItems: CartItem[]
  /**
   * Display names of the original order's items that could NOT be rebuilt
   * against the current catalog — missing item, no available offer, outside
   * its schedule, an option that no longer exists, or a mandatory option group
   * the order does not fill. The caller names them back to the customer so
   * they can re-add each one from the menu.
   */
  droppedItemNames: string[]
}

/**
 * Reconstructs a list of `CartItem`s from a previous `OrderResponse` by
 * looking each item up in the current catalog.
 *
 * The bar is deliberately all-or-nothing per item: a line is rebuilt only when
 * the live catalog accepts it EXACTLY as ordered — every option still exists
 * and every mandatory option group ends up filled. Anything short of that is
 * dropped and reported in `droppedItemNames`, never added half-configured:
 * checkout validates against the same catalog and would reject the whole order
 * (ORD-CHK-1008 / ORD-CHK-4020) at the last step instead.
 *
 * Pricing is recomputed from the live catalog — we intentionally ignore
 * the historical prices to avoid charging stale values.
 */
export function reconstructCartItemsFromOrder(
  order: OrderResponse,
  items: Item[],
  itemOffers: ItemOffer[],
  optionGroups: OptionGroup[],
  categories: Category[],
  availabilities: Availability[],
): ReorderResult {
  const cartItems: CartItem[] = []
  const droppedItemNames: string[] = []

  const dropItem = (orderItem: OrderItem, catalogItem: Item | null) => {
    droppedItemNames.push(catalogItem?.name ?? orderItem.name ?? "Item")
  }

  for (const orderItem of order.items ?? []) {
    const catalogItem = findCatalogItem(orderItem, items)
    if (!catalogItem) {
      console.info("[reorder-service] item dropped: not in catalog", {
        orderItemId: orderItem.id,
        name: orderItem.name,
        orderItem_itemId: orderItem.itemId,
        orderItem_merchantItemId: orderItem.merchantItemId,
        firstCatalogItem: items[0]
          ? { id: items[0].id, name: items[0].name }
          : null,
        catalogSize: items.length,
      })
      dropItem(orderItem, null)
      continue
    }

    const itemOffer = findAvailableItemOffer(catalogItem, itemOffers, categories)
    if (!itemOffer) {
      console.info("[reorder-service] item dropped: no AVAILABLE offer on this menu", {
        name: orderItem.name,
        catalogItemId: catalogItem.id,
      })
      dropItem(orderItem, catalogItem)
      continue
    }

    const category = findCategoryForOffer(categories, itemOffer.id)
    if (!isOfferAvailableNow(itemOffer, availabilities, category)) {
      console.info("[reorder-service] item dropped: outside availability schedule", {
        name: orderItem.name,
        catalogItemId: catalogItem.id,
        itemOfferId: itemOffer.id,
      })
      dropItem(orderItem, catalogItem)
      continue
    }

    const allowedOptionGroupIds = new Set(itemOffer.optionGroupsId ?? [])
    const allowedOptionGroups = optionGroups.filter(
      (og) => allowedOptionGroupIds.has(og.id) && og.status === "AVAILABLE",
    )

    const optionQuantities: CartItem["optionQuantities"] = {}
    const itemOptions: CartItem["itemOptions"] = {}
    const optionGroupsInUse: OptionGroup[] = []
    let cascadeInvalidate = false
    let missingOptionName: string | undefined

    for (const orderOption of orderItem.options ?? []) {
      const groupsWithRoom = allowedOptionGroups.filter(
        (group) => !isOptionGroupAtCapacity(group, optionQuantities[group.id]),
      )
      const scanOrder = [
        ...groupsWithRoom.filter((group) =>
          isOptionGroupBelowMinimum(group, optionQuantities[group.id]),
        ),
        ...groupsWithRoom.filter(
          (group) => !isOptionGroupBelowMinimum(group, optionQuantities[group.id]),
        ),
      ]

      let mapped = false
      for (const group of scanOrder) {
        const resolved = findCatalogOption(group, orderOption, items)
        if (!resolved) continue

        if (!optionQuantities[group.id]) optionQuantities[group.id] = {}
        optionQuantities[group.id][resolved.option.id] = orderOption.quantity
        itemOptions[resolved.option.id] = resolved.item
        if (!optionGroupsInUse.find((og) => og.id === group.id)) {
          optionGroupsInUse.push(group)
        }
        mapped = true
        break
      }
      if (!mapped) {
        cascadeInvalidate = true
        missingOptionName = orderOption.name ?? orderOption.itemId
        break
      }
    }

    if (cascadeInvalidate) {
      console.info("[reorder-service] item dropped: option unavailable (cascade)", {
        name: orderItem.name,
        catalogItemId: catalogItem.id,
        missingOption: missingOptionName,
      })
      dropItem(orderItem, catalogItem)
      continue
    }

    Object.assign(optionQuantities, normalizePortionGroups(optionGroupsInUse, optionQuantities))

    const invalidGroup = findInvalidOptionGroup(allowedOptionGroups, optionQuantities)
    if (invalidGroup) {
      console.info("[reorder-service] item dropped: option group no longer satisfied", {
        name: orderItem.name,
        catalogItemId: catalogItem.id,
        optionGroupId: invalidGroup.id,
        optionGroupName: invalidGroup.name,
        minPermitted: invalidGroup.minPermitted,
        maxPermitted: invalidGroup.maxPermitted,
        selected: optionQuantities[invalidGroup.id] ?? {},
      })
      dropItem(orderItem, catalogItem)
      continue
    }

    const unitPrice = computeItemTotal(itemOffer, optionGroupsInUse, optionQuantities)
    const itemTotalPrice = unitPrice * orderItem.quantity

    console.info(
      `[reorder-service] priced "${orderItem.name}" qty=${orderItem.quantity} ` +
        `unitPrice=${unitPrice} itemTotalPrice=${itemTotalPrice} ` +
        `itemOffer.price.value=${itemOffer.price.value} ` +
        `itemOffer.price.originalValue=${itemOffer.price.originalValue ?? "∅"}`,
    )

    cartItems.push({
      id: uuid(),
      item: catalogItem,
      itemOffer,
      itemOptions,
      optionGroups: optionGroupsInUse,
      optionQuantities,
      quantity: orderItem.quantity,
      itemTotalPrice,
      unitPrice,
      specialInstructions: "",
    })
  }

  return { cartItems, droppedItemNames }
}
